// Pure helpers for the Dictionary tab (kept out of the component so Jest can reach them).
// A facet is a list of accepted values (what the Hub writes); each one has current values (spellings found on
// products that get overwritten with it). Counts come from /dictionary/{facet}/counts, keyed by the exact spelling
// products hold (whitespace collapsed, case kept: Shopify treats "adidas" and "Adidas" as two brands).

export const normText = (t) => String(t ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
export const exactText = (t) => String(t ?? '').replace(/\s+/g, ' ').trim();

// One row per accepted value: its current values (an alias spelled exactly like the value is implied, so it is
// hidden; a case variant such as "Adidas" under "adidas" is a real current value and shows), how many products hold
// the accepted value today, and how many still hold one of its current values.
export function rows(facet, counts) {
  const c = counts || {};
  const byValue = {};
  for (const a of facet.aliases || []) {
    if (a.alias === a.value) continue;
    (byValue[a.value] = byValue[a.value] || []).push(a.alias);
  }
  return (facet.values || []).map((v) => {
    const current = byValue[v.value] || [];
    return {
      value: v.value,
      status: v.status,
      current,
      products: c[exactText(v.value)] || 0,
      stillCurrent: current.reduce((n, a) => n + (c[exactText(a)] || 0), 0),
    };
  });
}

const byText = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

// col 0 = accepted value (natural, case-insensitive), col 1 = products, col 2 = how many current values (ties by value).
export function sortedRows(list, sort) {
  const byValue = (a, b) => byText(a.value, b.value);
  const desc = sort.dir === 'descending';
  if (sort.col === 1) return [...list].sort((a, b) => (desc ? b.products - a.products : a.products - b.products) || byValue(a, b));   // ties stay by value
  if (sort.col === 2) return [...list].sort((a, b) => (desc ? b.current.length - a.current.length : a.current.length - b.current.length) || byValue(a, b));
  const out = [...list].sort(byValue);
  return desc ? out.reverse() : out;
}

// The Found on products list: col 0 = facet (by its label), col 1 = value found, col 2 = products (ties by value).
export function sortedFound(list, sort, labelOf) {
  const byValue = (a, b) => byText(a.value, b.value);
  const desc = sort.dir === 'descending';
  if (sort.col === 2) return [...list].sort((a, b) => (desc ? b.product_count - a.product_count : a.product_count - b.product_count) || byValue(a, b));
  if (sort.col === 0) return [...list].sort((a, b) => (desc ? -1 : 1) * byText(labelOf(a.facet), labelOf(b.facet)) || byValue(a, b));
  const out = [...list].sort(byValue);
  return desc ? out.reverse() : out;
}

const held = (n, value) => (n === 0 ? `No product holds "${value}" on Shopify` : `${n} product${n === 1 ? '' : 's'} hold${n === 1 ? 's' : ''} "${value}" on Shopify`);

// The confirmation dialog's body for rename, remove and fold.
export function confirmText(action, facetLabel, value, next, products, multi) {
  if (action === 'rename') {
    if (products === 0) return `${held(0, value)}. "${value}" becomes a current value under "${next}", and open proposals that say "${value}" are updated.`;
    return `${held(products, value)}. They are rewritten to "${next}" now, and "${value}" becomes a current value under it, so anything still tagged the old way is caught from here on. Open proposals that say "${value}" are updated too.`;
  }
  if (action === 'remove') {
    const how = multi ? `${facetLabel} is a list, so the other values on those products stay` : 'the field is cleared';
    const first = products === 0 ? `${held(0, value)}.` : `${held(products, value)}: it is taken off them now (${how}).`;
    return `${first} The Hub will not propose it again. If a vendor tags a product with it later, it shows up under Found on products, where you can bring it back or fold it into another value.`;
  }
  if (action === 'delete') {
    return `${held(0, value)}, so nothing changes there. The row leaves this list. If it is listed as a current value under another ${facetLabel}, that stays, so anything tagged that way is still rewritten.`;
  }
  if (products === 0) return `${held(0, value)}. "${value}" is kept as a current value under "${next}", so anything tagged that way is written as "${next}" from here on.`;
  return `${held(products, value)}. They are rewritten to "${next}" now, and "${value}" is kept as a current value under it.`;
}

// The Dictionary view in the address (?source=hub|taxonomy|found&facet=brand&sort=value|current&dir=asc, and
// fsort=facet|value&fdir=asc for the Found on products list): defaults are left out, so a plain
// /categorize-and-clean/dictionary is the default view and any other is a link to share.
const SOURCES = ['hub', 'taxonomy', 'found'];
const SORT_COLS = { value: 0, current: 2 };          // facet table; products (1) is the default
const FOUND_COLS = { facet: 0, value: 1 };           // found list; products (2) is the default
const colName = (map, col) => Object.keys(map).find((k) => map[k] === col) || '';
export function readParams(sp) {
  const source = sp.get('source');
  const dir = (k) => (sp.get(k) === 'asc' ? 'ascending' : 'descending');
  return {
    src: SOURCES.includes(source) ? source : 'all',
    facet: sp.get('facet') || '',
    sort: { col: SORT_COLS[sp.get('sort')] ?? 1, dir: dir('dir') },
    found: { col: FOUND_COLS[sp.get('fsort')] ?? 2, dir: dir('fdir') },
  };
}
export function writeParams(prev, changes) {
  const n = new URLSearchParams(prev);
  const put = (k, v) => { if (v) n.set(k, v); else n.delete(k); };
  if ('src' in changes) put('source', changes.src === 'all' ? '' : changes.src);
  if ('facet' in changes) put('facet', changes.facet);
  if ('sort' in changes) { put('sort', colName(SORT_COLS, changes.sort.col)); put('dir', changes.sort.dir === 'ascending' ? 'asc' : ''); }
  if ('found' in changes) { put('fsort', colName(FOUND_COLS, changes.found.col)); put('fdir', changes.found.dir === 'ascending' ? 'asc' : ''); }
  return n;
}
